import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { uniqueId } from 'lodash-es';
import { useSelector } from 'react-redux';

import {
  CACHE_EXPIRATION_TIME,
  CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
} from '@/features/constants';
import { MeteringPointActItemsParams } from '@/features/meteringPointActs/types/MeteringPointActItemsParams';
import {
  cachedItemByIdSelector,
  infiniteQueryOptions,
  paramsWithPageSize,
} from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { FiltersGrid, Pagination } from '../types';
import {
  MeteringPointAct,
  MeteringPointActDevice,
  MeteringPointActEquipment,
  MeteringPointActGrid,
  MeteringPointActModem,
  MeteringPointActPipelineSection,
  MeteringPointActSeal,
} from './types';

const tagTypes = [
  'MeteringPointAct',
  'MeteringPointActDevice',
  'MeteringPointActEquipment',
  'MeteringPointActModem',
  'MeteringPointActPipelineSection',
  'MeteringPointActSeal',
];

export const meteringPointActApi = createApi({
  reducerPath: 'meteringPointActApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/metering-point-acts',
  }),
  tagTypes,
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  endpoints: builder => ({
    getMeteringPointActs: builder.query<
      MeteringPointActGrid[],
      FiltersGrid | void
    >({
      query: (params = {}) => {
        return {
          url: `/query`,
          params,
        };
      },
      providesTags: () => ['MeteringPointAct', 'MeteringPointActs'],
    }),
    getMeteringPointActsI: builder.infiniteQuery<
      MeteringPointActGrid[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['MeteringPointAct', 'MeteringPointActs'],
    }),
    getMeteringPointActById: builder.query<MeteringPointAct, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['MeteringPointAct'],
    }),
    createMeteringPointAct: builder.mutation<
      MeteringPointAct & {
        connectedPowerCalculatorErrorMessage?: string;
        connectedPowerCalculatorMessage?: string;
      },
      FormData
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
        data,
      }),
      invalidatesTags: result => [
        'MeteringPointAct',
        { type: 'MeteringPointAct', id: result?.id },
      ],
    }),
    updateMeteringPointAct: builder.mutation<
      MeteringPointAct & {
        connectedPowerCalculatorErrorMessage?: string;
        connectedPowerCalculatorMessage?: string;
      },
      FormData
    >({
      query: data => ({
        url: `/${data.get('id')}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: tagTypes,
    }),
    deleteMeteringPointActById: builder.mutation<MeteringPointAct, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'MeteringPointAct',
        { type: 'MeteringPointAct', id },
      ],
    }),
    getMeteringPointActDevices: builder.query<
      MeteringPointActDevice[],
      MeteringPointActItemsParams
    >({
      query: params => ({
        url: `/devices`,
        params: paramsWithPageSize(params, 1000),
      }),
      providesTags: () => ['MeteringPointActDevice'],
      keepUnusedDataFor: CACHE_EXPIRATION_TIME,
    }),
    getMeteringPointActEquipment: builder.query<
      MeteringPointActEquipment[],
      MeteringPointActItemsParams
    >({
      query: params => ({
        url: `/equipment`,
        params: paramsWithPageSize(params, 1000),
      }),
      providesTags: () => ['MeteringPointActEquipment'],
      keepUnusedDataFor: CACHE_EXPIRATION_TIME,
      transformResponse: (response: MeteringPointActEquipment[]) => {
        // this id is for DataGrid only
        return response?.map(item => ({
          ...item,
          __id: uniqueId(),
        }));
      },
    }),
    getMeteringPointActModems: builder.query<
      MeteringPointActModem[],
      MeteringPointActItemsParams
    >({
      query: params => ({
        url: `/modems`,
        params: paramsWithPageSize(params, 1000),
      }),
      providesTags: () => ['MeteringPointActModem'],
      keepUnusedDataFor: CACHE_EXPIRATION_TIME,
    }),
    getMeteringPointActPipelineSections: builder.query<
      MeteringPointActPipelineSection[],
      MeteringPointActItemsParams
    >({
      query: params => ({
        url: `/pipeline-sections`,
        params: paramsWithPageSize(params, 1000),
      }),
      providesTags: () => ['MeteringPointActPipelineSection'],
      keepUnusedDataFor: CACHE_EXPIRATION_TIME,
    }),
    getMeteringPointActSeals: builder.query<
      MeteringPointActSeal[],
      MeteringPointActItemsParams
    >({
      query: params => ({
        url: `/seals`,
        params: paramsWithPageSize(params, 1000),
      }),
      providesTags: () => ['MeteringPointActSeal'],
      keepUnusedDataFor: CACHE_EXPIRATION_TIME,
    }),
  }),
});

export const {
  useGetMeteringPointActsQuery,
  useGetMeteringPointActByIdQuery,
  useCreateMeteringPointActMutation,
  useUpdateMeteringPointActMutation,
  useDeleteMeteringPointActByIdMutation,
  useGetMeteringPointActDevicesQuery,
  useGetMeteringPointActModemsQuery,
  useGetMeteringPointActEquipmentQuery,
  useGetMeteringPointActPipelineSectionsQuery,
  useGetMeteringPointActSealsQuery,
} = meteringPointActApi;

export const useGetMeteringPointActsInfiniteQuery = normalizeInfiniteQueryData(
  meteringPointActApi.useGetMeteringPointActsIInfiniteQuery,
);

const getCachedMeteringPointActByIdSelector = cachedItemByIdSelector(
  'meteringPointActApi',
  'getMeteringPointActsI',
);

export const useGetCachedMeteringPointActByIdSelector = id =>
  useSelector(getCachedMeteringPointActByIdSelector(id));
